// A2F-650: single horizontal pack card row for Step 3.
//
// Layout: icon square + name + tier badge + description on the left,
// toggle on the right. A 3px left accent stripe (green for FREE,
// orange for PRO) keeps the four rows scan-readable without breaking
// the consistent layout — the user reads "this one is included, those
// three are upgrades" without a divergent component shape.

import { Toggle } from './Toggle'

export type PackTier = 'FREE' | 'PRO'

interface PackRowProps {
  iconLetter: string
  name: string
  description: string
  tier: PackTier
  on: boolean
  // Disabled means the toggle is rendered but un-clickable. Used for
  // the IND Section Headings row which is always-on (built-in feature,
  // not a togglable pack).
  disabled?: boolean
  // A2F-764: render the row as a roadmap entry — replaces the
  // FREE/PRO tier badge with a "Coming soon" badge, dims the row,
  // and forces the toggle off + disabled. Used by /pro-v2 for packs
  // that are advertised but not yet shipping content (FDA terminology,
  // eCTD formatting). Honest disclosure for AppSource review while
  // keeping the user-facing roadmap visible.
  comingSoon?: boolean
  onToggle?: () => void
}

export function PackRow({
  iconLetter,
  name,
  description,
  tier,
  on,
  disabled = false,
  comingSoon = false,
  onToggle,
}: PackRowProps) {
  const isFree = tier === 'FREE'
  const accentBorder = comingSoon
    ? 'border-l-slate-300'
    : isFree
      ? 'border-l-emerald-700'
      : 'border-l-brand-orange'
  const rowOpacity = comingSoon ? 'opacity-60' : ''
  return (
    <div
      className={`flex items-center gap-3 bg-brand-canvas rounded-r-lg border border-l-[3px] border-slate-200 ${accentBorder} px-3.5 py-3 mb-2 ${rowOpacity}`}
    >
      <div className="grid place-items-center h-7 w-7 rounded-md bg-white border border-slate-200 text-xs font-semibold text-slate-500 shrink-0">
        {iconLetter}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-brand-charcoal truncate">
            {name}
          </span>
          {comingSoon ? (
            <span className="text-[9px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 bg-slate-100 text-slate-500">
              Coming soon
            </span>
          ) : isFree ? (
            <span className="text-[9px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 bg-emerald-50 text-emerald-700">
              Free
            </span>
          ) : (
            <span className="text-[9px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 bg-brand-orange/10 text-brand-orange">
              Pro
            </span>
          )}
        </div>
        <p className="text-[12px] text-slate-500 mt-0.5">{description}</p>
      </div>
      <Toggle
        on={comingSoon ? false : on}
        disabled={disabled || comingSoon}
        accent={isFree ? 'green' : 'orange'}
        onClick={onToggle}
        ariaLabel={`${on ? 'Disable' : 'Enable'} ${name}`}
      />
    </div>
  )
}
